/**
 * The application shell: rail, topbar, routed outlet, footer.
 *
 * Desktop is a two-column grid with a sticky rail; at ≤760px the rail leaves
 * the grid and becomes an off-canvas drawer behind the topbar menu button
 * (state here, presentation in `styles/responsive.css`).
 *
 * What the shell owns, so no page has to:
 *   - the skip link and the `<main>` landmark
 *   - the breadcrumb trail and per-route focus reset
 *   - the command palette (⌘K) and its trigger
 *   - connection, background-job and notification status
 *   - the practice disclaimer and copyright in the footer
 *   - a per-page error boundary keyed by page family
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { initials } from '@/lib/format';
import { logger } from '@/lib/logger';
import { cx } from '@/lib/cx';
import { BackgroundJobs, ConnectionStrip, NetworkPulse } from './ActivityIndicator';
import { AppFooter } from './AppFooter';
import { Breadcrumbs } from './Breadcrumbs';
import { CommandPalette, useCommandPalette } from './CommandPalette';
import { ErrorBoundary } from './ErrorBoundary';
import { Icon } from './icons';
import { NotificationBell } from './NotificationBell';
import { ADMIN_NAV, TOOL_GROUPS, TOOL_NAV, pageFamily, type NavItem } from './nav';
import { ThemeToggle } from './ThemeToggle';
import { Menu } from './ui';

// Re-exported so existing imports (and tests) keep resolving through Layout.
export { TOOL_GROUPS, TOOL_NAV, ADMIN_NAV, pageFamily };
export type { NavItem };

/**
 * Page families whose screen fills the viewport rather than flowing down it.
 * Chat's thread scrolls internally and pins its composer, so it must be sized
 * against the space left between the topbar and the footer — not given a fixed
 * height that a page header or the footer can push off-screen.
 */
const FILL_FAMILIES = new Set(['chat']);

/** Mac users expect ⌘K; everyone else expects Ctrl K. */
function shortcutLabel(): string {
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    '';
  return /mac|iphone|ipad/i.test(platform) ? '⌘K' : 'Ctrl K';
}

function NavRow({ item, locked }: { item: NavItem; locked?: boolean }) {
  const IconCmp = Icon[item.icon];
  if (locked) {
    return (
      <NavLink to="/login" className="nav-link locked" title={`Sign in to use ${item.label}`}>
        <IconCmp className="ico" aria-hidden />
        <span>{item.label}</span>
        <span className="lock-hint">Sign in</span>
      </NavLink>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) => cx('nav-link', isActive && 'active')}
      title={item.description}
    >
      <IconCmp className="ico" aria-hidden />
      <span>{item.label}</span>
    </NavLink>
  );
}

/** The wordmark, shared by the rail and the auth screens. */
export function BrandMark({ size = 30 }: { size?: number }) {
  return (
    <svg className="brand-mark" width={size} height={size} viewBox="0 0 32 32" aria-hidden focusable="false">
      <rect width="32" height="32" rx="5" fill="var(--ink)" />
      <rect x="6" y="6" width="4" height="20" fill="var(--paper)" />
      <rect x="6" y="6" width="14" height="4" fill="var(--paper)" />
      <rect x="6" y="14" width="11" height="4" fill="var(--paper)" />
      <rect x="22" y="6" width="4" height="20" fill="var(--accent)" />
    </svg>
  );
}

export function Layout() {
  const { user, status, isAdmin, isStaff, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const palette = useCommandPalette();
  const mainRef = useRef<HTMLElement | null>(null);
  const firstRender = useRef(true);

  const authed = status === 'authenticated';
  const anon = status === 'anonymous';
  const adminItems = ADMIN_NAV.filter((i) => (i.admin ? isAdmin : i.staff ? isStaff : true));

  // Mobile drawer: opened by the topbar menu button, closed by the scrim or by
  // navigating anywhere. Desktop ignores this entirely.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Route change: move focus to the content region so a keyboard or screen
  // reader user does not have to tab back through the whole rail. Skipped on
  // first paint, where the browser's own focus is correct.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
    logger.child('nav').debug('Route change', { path: location.pathname });
  }, [location.pathname]);

  return (
    <div className="app">
      <a className="skip-link" href="#main">Skip to content</a>
      {menuOpen && <div className="sidebar-scrim" onClick={() => setMenuOpen(false)} aria-hidden />}

      <aside className={cx('sidebar', menuOpen && 'open')}>
        <NavLink to="/" className="brand">
          <BrandMark />
          <span className="stack">
            <span className="brand-name">ALFIE</span>
            <span className="brand-sub">Legal Intelligence</span>
          </span>
        </NavLink>

        <nav className="nav-scroll" aria-label="Tools">
          {TOOL_GROUPS.map((g) => (
            <div className="nav-group" key={g.label}>
              <div className="nav-group-label">{g.label}</div>
              {g.items.map((i) => <NavRow key={i.to} item={i} locked={anon && i.gated} />)}
            </div>
          ))}

          {adminItems.length > 0 && (
            <div className="nav-group">
              <div className="nav-group-label">Administration</div>
              {adminItems.map((i) => <NavRow key={i.to} item={i} />)}
            </div>
          )}
        </nav>

        <div className="sidebar-foot">
          <NavLink to="/contact" className={({ isActive }) => cx('nav-link', isActive && 'active')}>
            <Icon.Send className="ico" aria-hidden />
            <span>Contact</span>
          </NavLink>
          {authed ? (
            <>
              <NavLink to="/settings" className={({ isActive }) => cx('nav-link', isActive && 'active')}>
                <Icon.Users className="ico" aria-hidden />
                <span>Account</span>
              </NavLink>
              <button type="button" className="nav-link w-full" onClick={() => void logout()}>
                <Icon.Logout className="ico" aria-hidden />
                <span>Sign out</span>
              </button>
            </>
          ) : anon ? (
            <>
              <NavLink to="/login" className={({ isActive }) => cx('nav-link', isActive && 'active')}>
                <Icon.Users className="ico" aria-hidden />
                <span>Log in</span>
              </NavLink>
              <NavLink to="/register" className={({ isActive }) => cx('nav-link', isActive && 'active')}>
                <Icon.Plus className="ico" aria-hidden />
                <span>Create account</span>
              </NavLink>
            </>
          ) : null}
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="icon-btn menu-btn bordered"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <Icon.Menu aria-hidden />
          </button>

          <Breadcrumbs />

          <div className="topbar-actions">
            <button type="button" className="omni-trigger" onClick={() => palette.setOpen(true)}>
              <Icon.Search aria-hidden />
              <span>Search tools</span>
              <kbd className="kbd">{shortcutLabel()}</kbd>
            </button>
            <NetworkPulse />
            <BackgroundJobs />
            {authed && <NotificationBell />}
            <ThemeToggle />
            {authed ? (
              <Menu
                label="Account"
                items={[
                  { label: user?.email ?? user?.username ?? 'Signed in', heading: true },
                  { key: 'account', label: 'Account settings', icon: 'Users', onSelect: () => navigate('/settings') },
                  { key: 'org', label: 'Organization', icon: 'Library', onSelect: () => navigate('/orgs') },
                  { key: 'status', label: 'System status', icon: 'Activity', onSelect: () => navigate('/status') },
                  'separator',
                  { key: 'out', label: 'Sign out', icon: 'Logout', danger: true, onSelect: () => void logout() },
                ]}
                trigger={({ toggle, ref, open }) => (
                  <button
                    ref={ref}
                    type="button"
                    className="userchip"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                  >
                    <span className="avatar">{initials(user?.displayName ?? user?.username)}</span>
                    <span className="stack">
                      <span className="who">{user?.displayName ?? user?.username}</span>
                      <span className="muted t-micro">{(user?.roles ?? []).join(' · ')}</span>
                    </span>
                    <Icon.ChevronDown aria-hidden style={{ width: 13, height: 13, color: 'var(--ink-4)' }} />
                  </button>
                )}
              />
            ) : anon ? (
              <div className="row gap-2">
                <NavLink to="/login" className="btn ghost sm">Log in</NavLink>
                <NavLink to="/register" className="btn sm">Sign up</NavLink>
              </div>
            ) : null}
          </div>
        </header>

        <ConnectionStrip />

        {/* Keyed per page family: a crash on one page keeps the shell alive and
            navigating to another tool resets the boundary — while moving
            between a tool's list and one of its items (/ ↔ /chat/:id,
            /drafter ↔ /drafter/:id) keeps the page mounted, so an in-flight
            chat stream survives its conversation being given a URL. */}
        <main
          className={cx('content', FILL_FAMILIES.has(pageFamily(location.pathname)) && 'fill')}
          id="main"
          ref={mainRef}
          tabIndex={-1}
        >
          <ErrorBoundary key={pageFamily(location.pathname)} label="This page">
            <Outlet />
          </ErrorBoundary>
        </main>

        <AppFooter />
      </div>

      <CommandPalette open={palette.open} onClose={palette.close} />
    </div>
  );
}
